import React, { useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { TextField, InputAdornment } from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs, { Dayjs } from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { useFormContext } from 'react-hook-form';
dayjs.extend(quarterOfYear);

const predefinedRanges = [
  {
    label: 'Today',
    value: 'Today',
    getRange: (): [Dayjs, Dayjs] => [dayjs(), dayjs()],
  },
  {
    label: 'Yesterday',
    value: 'Yesterday',
    getRange: (): [Dayjs, Dayjs] => [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')],
  },
  {
    label: 'Tomorrow',
    value: 'Tomorrow',
    getRange: (): [Dayjs, Dayjs] => [dayjs().add(1, 'day'), dayjs().add(1, 'day')],
  },
  {
    label: 'Last 7 days',
    value: 'Last 7 days',
    getRange: (): [Dayjs, Dayjs] => [dayjs().subtract(6, 'day'), dayjs()],
  },
  {
    label: 'This Week',
    value: 'This Week',
    getRange: (): [Dayjs, Dayjs] => [dayjs().startOf('week'), dayjs().endOf('week')],
  },
  {
    label: 'Last Week',
    value: 'Last Week',
    getRange: (): [Dayjs, Dayjs] => [
      dayjs().subtract(1, 'week').startOf('week'),
      dayjs().subtract(1, 'week').endOf('week'),
    ],
  },
  {
    label: 'Last Month',
    value: 'Last Month',
    getRange: (): [Dayjs, Dayjs] => [
      dayjs().subtract(1, 'month').startOf('month'),
      dayjs().subtract(1, 'month').endOf('month'),
    ],
  },

  {
    label: 'Last Quarter',
    value: 'Last Quarter',
    getRange: (): [Dayjs, Dayjs] => {
      const currentQuarter = Math.floor(dayjs().month() / 3); // Get the current quarter (0-3)
      const startOfLastQuarter = dayjs().subtract(1, 'quarter').startOf('quarter'); // Start of last quarter
      const endOfLastQuarter = dayjs(startOfLastQuarter).endOf('quarter'); // End of last quarter
      return [startOfLastQuarter, endOfLastQuarter];
    },
  },
  {
    label: 'Quarter to Date',
    value: 'Quarter to Date',
    getRange: (): [Dayjs, Dayjs] => {
      const startOfCurrentQuarter = dayjs().startOf('quarter'); // Start of current quarter
      const today = dayjs(); // Current date
      return [startOfCurrentQuarter, today];
    },
  },

  {
    label: 'Last Year',
    value: 'Last Year',
    getRange: (): [Dayjs, Dayjs] => [
      dayjs().subtract(1, 'year').startOf('year'),
      dayjs().subtract(1, 'year').endOf('year'),
    ],
  },
  {
    label: 'Month to Date',
    value: 'Month to Date',
    getRange: (): [Dayjs, Dayjs] => [dayjs().startOf('month'), dayjs()],
  },

  {
    label: 'Year to Date',
    value: 'Year to Date',
    getRange: (): [Dayjs, Dayjs] => [dayjs().startOf('year'), dayjs()],
  },
];

const DateRangePickerWithName = ({ label, onChange, defaultValue, name }: any) => {
  const { setValue, watch } = useFormContext();
  const [selectedRange, setSelectedRange] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const datePickerRef = useRef<any>(null);

  const handlePredefinedRangeClick = (range: { label: string; getRange: () => [Dayjs, Dayjs] }) => {
    const [start, end] = range.getRange();
    setStartDate(start.toDate());
    setEndDate(end.toDate());
    setSelectedRange(range.label);

    if (datePickerRef?.current) {
      datePickerRef.current.setPreSelection(start.toDate());
    }

    // Update form values
    setValue(name, range.label);
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedRange('');
    setStartDate(date);
    setEndDate(null); // Reset endDate for single date selection

    if (datePickerRef?.current) {
      datePickerRef.current.setPreSelection(dayjs(date)?.toDate());
    }

    setValue(name, dayjs(date)?.format('MM/DD/YYYY'));
    // setValue(name, {
    //   startDate: start,
    //   endDate: end,
    //   rangeLabel: null, // No predefined range selected
    // });
  };

  const renderCalendarWithPredefinedRanges = ({
    className,
    children,
  }: {
    className: string;
    children: React.ReactNode;
  }) => (
    <div className="custom-calendar-container" style={{ display: 'flex', padding: '10px' }}>
      {/* Predefined Ranges */}
      <div className="custBtnFilter" style={{ borderRight: '1px solid #ddd', padding: '10px', width: '200px' }}>
        {predefinedRanges.map((range) => (
          <button
            className="btn btn-primary"
            key={range.value}
            onClick={() => handlePredefinedRangeClick(range)}
            style={{
              display: 'block',
              width: '100%',
              padding: '8px 10px',
              margin: '5px 0',
              backgroundColor: selectedRange === range.label ? '#0000b5' : '#ffffff',
              color: selectedRange === range.label ? 'white' : 'black',
              border: '1px solid #ddd',
              borderRadius: '4px',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            {range.label}
          </button>
        ))}
      </div>
      {/* Calendar */}
      <div className={className} style={{ flexGrow: 1, padding: '10px' }}>
        {children}
      </div>
    </div>
  );

  return (
    <>
      {selectedRange === '' ? (
        // Single date picker
        <DatePicker
          ref={datePickerRef}
          selected={startDate}
          onCalendarClose={() => {
            if (!startDate) {
              setEndDate(dayjs()?.toDate()); // Convert to a Date object
            }
          }}
          onChange={(date: Date | null) => handleDateChange(date)}
          calendarContainer={renderCalendarWithPredefinedRanges}
          customInput={
            <TextField
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarMonth style={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              label="Select Date"
              fullWidth
              size="small"
              value={startDate ? dayjs(startDate).format('MM/DD/YYYY') : ''}
            />
          }
          dateFormat="MM/dd/yyyy"
          value={defaultValue}
        />
      ) : (
        <DatePicker
          ref={datePickerRef}
          selected={startDate}
          startDate={!startDate ? undefined : startDate}
          endDate={!endDate ? undefined : endDate}
          onChange={(dates: [Date | null, Date | null]) => {
            setStartDate(dates[0]);
            setEndDate(dates[1]);
            setSelectedRange('');

            if (dates[0]) {
              setValue(name, dayjs(dates[0])?.format('MM/DD/YYYY'));
            }
          }}
          selectsRange={true}
          calendarContainer={renderCalendarWithPredefinedRanges}
          onCalendarClose={() => {
            if (!endDate && startDate) {
              setEndDate(startDate);
            }
          }}
          customInput={
            <TextField
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarMonth style={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
                // readOnly: true,
              }}
              label="Select Date Range"
              fullWidth
              size="small"
              value={
                startDate && endDate
                  ? `${dayjs(startDate).format('MM/DD/YYYY')} - ${dayjs(endDate).format('MM/DD/YYYY')}`
                  : startDate
                    ? dayjs(startDate).format('MM/DD/YYYY')
                    : ''
              }
            />
          }
          dateFormat="MM/dd/yyyy"
          value={defaultValue}
        />
      )}
    </>
  );
};

export default DateRangePickerWithName;
